import { FC, useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import dayjs from 'dayjs'
import { useSnackbar } from 'notistack'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import {
  GuardaPrivacidade,
  mensagemErroPrivacidade,
  rotuloAtor,
  useAcessoPrivacidade,
} from '@/components/privacidade/comum'
import { privacidadeService } from '@/services/privacidadeService'
import type { PageResponse } from '@/types/comum'
import { MOTIVO_INCLUSAO_LABEL, RegistroExpurgo, rotuloCategoria } from '@/types/privacidade'
import { formatDateTime } from '@/utils/formatters'

const ROTULO_EVENTO: Record<string, string> = {
  SOLICITADO: 'Solicitado',
  APROVADO_1: '1ª aprovação',
  APROVADO_2: '2ª aprovação',
  REJEITADO: 'Rejeitado',
  BLOQUEADO_RETENCAO: 'Bloqueado por retenção legal',
  EXECUTADO: 'Executado',
  CONCLUIDO: 'Concluído',
  REAPLICADO: 'Reaplicado',
}

const rotuloEvento = (evento: string): string =>
  evento.startsWith('COPIA_CONFIRMADA:')
    ? `Cópia confirmada (${evento.slice('COPIA_CONFIRMADA:'.length)})`
    : ROTULO_EVENTO[evento] ?? evento

const Conteudo: FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const acesso = useAcessoPrivacidade()
  const [pagina, setPagina] = useState(0)
  const [tamanho, setTamanho] = useState(50)
  const [dados, setDados] = useState<PageResponse<RegistroExpurgo> | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [exportando, setExportando] = useState(false)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      setDados(await privacidadeService.registros(pagina, tamanho))
    } catch (e) {
      setErro(mensagemErroPrivacidade(e, 'Falha ao carregar a trilha de expurgos'))
      setDados(null)
    } finally {
      setCarregando(false)
    }
  }, [pagina, tamanho])

  useEffect(() => {
    carregar()
  }, [carregar])

  const exportar = async () => {
    setExportando(true)
    try {
      const blob = await privacidadeService.exportarRegistrosCsv()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `trilha-expurgos-${dayjs().format('YYYY-MM-DD')}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e) {
      enqueueSnackbar(mensagemErroPrivacidade(e, 'Falha ao exportar'), { variant: 'error' })
    } finally {
      setExportando(false)
    }
  }

  const linhas = dados?.content ?? []

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Trilha de expurgos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Somente leitura e imutável (o banco recusa alteração e exclusão). Sem nome de arquivo, texto ou dado
            pessoal por construção.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={exportar}
          disabled={exportando}
          sx={{ flexShrink: 0 }}
        >
          Exportar CSV
        </Button>
      </Stack>

      {carregando && !dados ? (
        <EstadoDeLista estado="carregando" />
      ) : erro ? (
        <EstadoDeLista estado="erro" detalhe={erro} acao={{ label: 'Tentar de novo', onClick: carregar }} />
      ) : dados && linhas.length === 0 ? (
        <EstadoDeLista estado="vazio" mensagem="Nenhum registro de expurgo" detalhe="Nenhum expurgo foi pedido ainda." />
      ) : dados ? (
        <Paper elevation={0}>
          <TableContainer>
            <Table size="small" aria-label="Trilha de expurgos">
              <TableHead>
                <TableRow>
                  <TableCell>Quando</TableCell>
                  <TableCell>Evento</TableCell>
                  <TableCell>Expurgo</TableCell>
                  <TableCell>Documento</TableCell>
                  <TableCell>Categoria</TableCell>
                  <TableCell>Inclusão</TableCell>
                  <TableCell>Protocolo</TableCell>
                  <TableCell>Ator</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {linhas.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{formatDateTime(r.ocorridoEm)}</TableCell>
                    <TableCell>
                      {rotuloEvento(r.evento)}
                      {r.detalhe ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {r.detalhe}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{r.expurgoId.slice(0, 8)}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {r.documentoId ? r.documentoId.slice(0, 8) : '—'}
                      {r.versao != null ? ` · v${r.versao}` : ''}
                    </TableCell>
                    <TableCell>{r.tipoEntidade ? rotuloCategoria(r.tipoEntidade) : '—'}</TableCell>
                    <TableCell>{r.motivoInclusao ? MOTIVO_INCLUSAO_LABEL[r.motivoInclusao] : '—'}</TableCell>
                    <TableCell>{r.solicitacaoProtocolo ?? '—'}</TableCell>
                    <TableCell>{rotuloAtor(r.ator, acesso.usuarioId)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={dados.totalElements}
            page={pagina}
            rowsPerPage={tamanho}
            rowsPerPageOptions={[25, 50, 100]}
            onPageChange={(_, p) => setPagina(p)}
            onRowsPerPageChange={(e) => {
              setTamanho(Number(e.target.value))
              setPagina(0)
            }}
            labelRowsPerPage="Por página"
          />
        </Paper>
      ) : null}
    </Box>
  )
}

/** Trilha de expurgos (AN-12 §5.5): somente leitura + CSV; ADMIN e DPO. */
export const RegistrosExpurgoPage: FC = () => (
  <GuardaPrivacidade>
    <Conteudo />
  </GuardaPrivacidade>
)

export default RegistrosExpurgoPage
